import styles from "./IntegratedCTA.module.scss";
import {
  SecondaryButton,
  UnderlinedButton,
} from "@JohnLewisPartnership/jl-design-system/dist/components/button/Button";
import { SaleTagFilled } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/SaleTagFilled";
import { useTradeInContext } from "../../TradeInContext";
import { dispatchCustomEvent } from "../../../../utils/customEvent/dispatchCustomEvent";
import { TradeInEvents } from "../../TradeIn";
import classNames from "classnames/bind";
import { formatAmplienceImageURL } from "@JohnLewisPartnership/jl-design-system/dist/utils/imageUrlModifier/imageUrlModifier";

const cx = classNames.bind(styles);

type Props = {
  isFullWidthMode: boolean;
  isTradeInModelLoading: boolean;
  onClick: () => void;
};

export const IntegratedCTAUI = ({
  isFullWidthMode,
  isTradeInModelLoading,
  onClick,
}: Props) => {
  const { productImage, promotionValue, hasPromoCode, flags } =
    useTradeInContext();
  const showNewTradeInUI = flags.showNewTradeInUI.value === true;
  const dispatchTradeInReadMoreEvent = () => {
    dispatchCustomEvent(TradeInEvents.TRADE_IN_READ_MORE_EVENT);
  };

  const PromoMessageBlock = () => (
    <>
      {!!promotionValue && (
        <div className={styles.cta__promo}>
          <p className={styles.cta__promoMessage}>
            <SaleTagFilled size="medium" className={styles.saleTag} />
            {hasPromoCode ? (
              <span>Receive an extra {promotionValue} off with promo code</span>
            ) : (
              <span>Get an extra {promotionValue} when you trade-in</span>
            )}
          </p>
          <UnderlinedButton
            tag="a"
            role="button"
            onClick={() => {
              dispatchTradeInReadMoreEvent();
            }}
            labelText="Read more"
            className={styles.cta__promoButton}
          />
        </div>
      )}
    </>
  );

  return (
    <div
      className={cx(styles.cta, {
        fullWidthMode: isFullWidthMode,
        "cta-new": showNewTradeInUI,
      })}
      data-testid="integrated-cta"
    >
      {showNewTradeInUI ? (
        <>
          {/* Setting the product image thumbnail to wid=72 pixel ensure that Amplience serve the image at its native portrait aspect ratio —*/}
          {/* no square crop that distorts the product */}
          {/* Height is now dictated by the natural image ratio via CSS height: auto */}
          <img
            src={formatAmplienceImageURL(
              `${productImage}?fmt=auto&$background-off-white$&wid=72`,
            )}
            srcSet={`${formatAmplienceImageURL(`${productImage}?fmt=auto&$background-off-white$&wid=144`)} 2x`}
            width={72}
            alt="Product image"
            className={styles["cta-new__productImage"]}
          />
          <h2 className={styles.cta__title}>
            Get up to £50 when you trade in your old device.
          </h2>
          <UnderlinedButton
            className={styles.cta__button}
            onClick={onClick}
            submitting={isTradeInModelLoading}
            labelText="Get a free estimate now"
          />
          <PromoMessageBlock />
        </>
      ) : (
        <>
          <h2 className={styles.cta__title}>
            Got a device you are no longer using?
          </h2>
          <p className={styles.cta__description}>
            Trade-in now and get some money back when making your new purchase
          </p>
          <SecondaryButton
            onClick={onClick}
            submitting={isTradeInModelLoading}
            labelText="Trade in device"
            width="full-width"
            className={styles.cta__tradeInButton}
          />
          <PromoMessageBlock />
        </>
      )}
    </div>
  );
};
